import { PolySynth, AMSynth } from 'tone';
import type { NoteName } from 'shared/definitions/musicTheory/notesAndAccidentals';
import type Chord from './chord';
import { EqualTemperament } from './temperaments/equalTemperament';
import { anythingToMidiNumbers, beatToTimeMS, AbstractInstrument } from './abstractInstrument';
import type Note from './note';
import type NoteHolder from './noteHolder';
import type { MidiMessage, PlayOptions } from './soundBufferHolder';
import type { Temperament } from './temperaments/temperament';

export class FMSynthInstrument extends AbstractInstrument {
  private synth: PolySynth = new PolySynth(AMSynth, {
    harmonicity: 1,
  }).toDestination();

  private temperament: Temperament = new EqualTemperament();

  private activeMidis: { [midi: number]: { velocity: number; stopTime?: number } } = {};

  public setTemperament(
    temperament: Temperament,
    temperamentBase?: NoteName | Note,
    temperamentBaseFrequency?: number
  ) {
    if (!temperament) {
      console.error('Expected temperament to be a Temperament, got:', temperament);
      throw Error(`Temperament must be defined. See console for details`);
    }
    if (temperamentBase) temperament.setBase(temperamentBase, temperamentBaseFrequency);
    this.temperament = temperament;

    this.cleanActiveMidis();
    // Stop all sounding ntoes:
    this.synth.releaseAll();
    // Reattack all notes with new tuning
    Object.entries(this.activeMidis).forEach(([midi, { velocity, stopTime }]) => {
      if (typeof stopTime === 'number') {
        // Convert stopTime to a druation from now:
        stopTime -= this.synth.context.now();
      }
      // Restart the note
      this.start(Number(midi), { stopTime, velocity });
    });
  }

  public noteOn(
    midiNumber: number | Note | Chord | NoteHolder,
    velocity?: number
  ): AbstractInstrument {
    const midis = anythingToMidiNumbers(midiNumber);
    midis.forEach((midi) => this.start(midi, { velocity }));
    return this;
  }

  public noteOff(midiNumber: number | Note | Chord | NoteHolder) {
    const midis = anythingToMidiNumbers(midiNumber);
    midis.forEach((midi) => this.stop(midi));
    return this;
  }

  public allNotesOff() {
    this.synth.releaseAll();
    this.activeMidis = {};
    return this;
  }

  public setMuted(muted: boolean) {
    this.synth.volume.rampTo(muted ? -Infinity : 0, 0.25);
  }

  public play(toPlay: number | Note | Chord | NoteHolder, playOptions: PlayOptions = {}) {
    if (typeof toPlay === 'number') {
      return this.start(toPlay, playOptions);
    }

    const midiMessages = toPlay.getMidiMessages();
    midiMessages.forEach((message) => {
      if (typeof message.midiNumber !== 'number') return;
      this.play(message.midiNumber, playOptions);
    });
    return this;
  }

  public playMidiMessages(midiMessages: MidiMessage[], tempo?: number, startTime?: number) {
    midiMessages.forEach((message) => {
      if (typeof message.midiNumber !== 'number') return;

      this.play(message.midiNumber, {
        startTime: beatToTimeMS(message.startBeat, tempo, startTime),
        stopTime: beatToTimeMS(message.stopBeat, tempo, startTime),
      });
    });
    return this;
  }

  private cleanActiveMidis() {
    const now = this.synth.context.now();
    Object.entries(this.activeMidis).forEach(([midi, info]) => {
      if (info.stopTime && info.stopTime < now) {
        delete this.activeMidis[Number(midi)];
      }
    });
  }

  private start(midi: number, playOptions: PlayOptions = {}) {
    this.cleanActiveMidis();
    console.log(`Start ${midi}`);
    const { startTime, stopTime } = this.getRelativeTimes(playOptions, this.synth.context.now());
    const frequency = this.temperament.getFrequency(midi);
    const velocity = playOptions.velocity ?? 0.5;
    const now = this.synth.context.now();

    if (typeof stopTime === 'number') {
      const duration = stopTime - startTime;
      this.synth.triggerAttackRelease(frequency, duration, startTime + now, playOptions.velocity);
      this.activeMidis[midi] = {
        velocity,
        stopTime: stopTime + now,
      };

      return this;
    }

    this.synth.triggerAttack(frequency, startTime + now, playOptions.velocity);
    this.activeMidis[midi] = {
      velocity,
    };

    return this;
  }

  private stop(midi: number) {
    console.log(`Stop ${midi}`);
    const frequency = this.temperament.getFrequency(midi);
    this.synth.triggerRelease(frequency);
    delete this.activeMidis[midi];
    return this;
  }
}
